import { useEffect } from 'react'

/**
 * Stops the page behind an open sheet from scrolling. `overflow: hidden` alone does not hold on
 * iOS Safari (the page still rubber-bands under a finger), so the body is pinned with
 * `position: fixed` at the current offset and put back where it was on release. Locks nest: the
 * page unpins only when the last holder lets go. `overflow` is left alone on purpose: useFocusTrap
 * already sets and restores it, and two owners restoring it in either order leaves it stuck.
 */
let holders = 0
let saved: { scrollY: number; style: Partial<CSSStyleDeclaration> } | null = null

function lock() {
  holders += 1
  if (holders > 1) return
  const { body } = document
  const scrollY = window.scrollY
  saved = {
    scrollY,
    style: { position: body.style.position, top: body.style.top, left: body.style.left, right: body.style.right, width: body.style.width },
  }
  body.style.position = 'fixed'
  body.style.top = `-${scrollY}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'
}

function unlock() {
  holders = Math.max(0, holders - 1)
  if (holders > 0 || !saved) return
  const { body } = document
  Object.assign(body.style, saved.style)
  if (saved.scrollY > 0) window.scrollTo(0, saved.scrollY)
  saved = null
}

export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined
    lock()
    return unlock
  }, [active])
}
